import { useState, useEffect } from "react";
import { Instagram, Twitter, Hash, ArrowUpRight, MapPin, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";

export const Footer = () => {
  const [socials, setSocials] = useState({ social_instagram: "", social_twitter: "", social_threads: "" });

  useEffect(() => {
    axios.get(`${API}/seo`).then(r => setSocials(r.data)).catch(() => {});
  }, []);

  const socialLinks = [
    { url: socials.social_instagram || "https://www.instagram.com/propix3ls?igsh=MXJsMmRxaWNhYzdlYw==", icon: <Instagram className="w-4 h-4" />, label: "Instagram", testId: "footer-instagram" },
    { url: socials.social_twitter || "https://x.com/Propix3ls", icon: <Twitter className="w-4 h-4" />, label: "Twitter / X", testId: "footer-twitter" },
    { url: socials.social_threads || "https://www.threads.com/@propix3ls?igshid=NTc4MTIwNjQ2YQ=", icon: <Hash className="w-4 h-4" />, label: "Threads", testId: "footer-threads" },
  ];

  const exploreLinks = [
    { to: "/portfolio", label: "Portfolio" },
    { to: "/booking", label: "Book a Shoot" },
    { to: "/blog", label: "Blog" },
    { to: "/ebooks", label: "E-Books" },
    { to: "/collaborate", label: "Collaborate" },
    { to: "/magazine", label: "Magazine" },
    { to: "/about-us", label: "About Us" },
    { to: "/contact-us", label: "Contact Us" },
  ];

  const locationLinks = [
    { to: "/lifestyle-photographer-london", label: "London, UK", testId: "footer-london" },
    { to: "/ugc-content-creator-usa", label: "United States", testId: "footer-usa" },
    { to: "/lifestyle-content-canada", label: "Canada", testId: "footer-canada" },
  ];

  const legalLinks = [
    { to: "/terms", label: "Terms", testId: "footer-terms" },
    { to: "/privacy", label: "Privacy", testId: "footer-privacy" },
    { to: "/collaboration-policy", label: "Collab Policy", testId: "footer-collab-policy" },
    { to: "/takedown", label: "Takedown", testId: "footer-takedown" },
  ];

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="relative mt-auto" data-testid="footer">
      {/* Top gradient line */}
      <div className="h-px bg-gradient-to-r from-transparent via-[#D946EF]/30 to-transparent" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-14 md:py-20">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-10 md:gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-4 lg:col-span-4">
              <Link to="/" className="inline-block mb-5 group">
                <img
                  src="/logo.png"
                  alt="PRO PIX3LS"
                  className="h-8 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </Link>
              <p className="text-zinc-500 text-sm font-['Manrope'] leading-relaxed max-w-xs mb-6">
                Visuals That Pulse. High-quality photography and cinematic reels for creators worldwide.
              </p>

              {/* Social icons */}
              <div className="flex items-center gap-2">
                {socialLinks.map(s => (
                  <a
                    key={s.testId}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full bg-white/[0.04] hover:bg-[#D946EF]/15 flex items-center justify-center text-zinc-500 hover:text-[#D946EF] transition-all duration-300"
                    data-testid={s.testId}
                    aria-label={s.label}
                  >
                    {s.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Explore */}
            <div className="col-span-1 md:col-span-2 lg:col-span-2">
              <h4 className="font-['Syne'] font-bold text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5">
                Explore
              </h4>
              <div className="flex flex-col gap-2.5">
                {exploreLinks.map(l => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="text-zinc-500 hover:text-white text-sm font-['Manrope'] transition-colors duration-200 w-fit"
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Locations */}
            <div className="col-span-1 md:col-span-3 lg:col-span-3">
              <h4 className="font-['Syne'] font-bold text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5">
                Where We Shoot
              </h4>
              <div className="flex flex-col gap-2.5">
                {locationLinks.map(l => (
                  <Link
                    key={l.to}
                    to={l.to}
                    className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-['Manrope'] transition-colors duration-200 group w-fit"
                    data-testid={l.testId}
                  >
                    <MapPin className="w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity" />
                    {l.label}
                  </Link>
                ))}
                <span className="flex items-center gap-2 text-zinc-600 text-xs font-['Manrope'] mt-1">
                  <Sparkles className="w-3 h-3 opacity-30" />
                  + travel weeks worldwide
                </span>
              </div>
            </div>

            {/* Connect */}
            <div className="col-span-2 md:col-span-3 lg:col-span-3">
              <h4 className="font-['Syne'] font-bold text-xs uppercase tracking-[0.2em] text-zinc-400 mb-5">
                Connect
              </h4>
              <div className="flex flex-col gap-2.5">
                {socialLinks.map(s => (
                  <a
                    key={`link-${s.testId}`}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2.5 text-zinc-500 hover:text-white text-sm font-['Manrope'] transition-colors duration-200 group w-fit"
                  >
                    <span className="opacity-50 group-hover:opacity-100 transition-opacity">{s.icon}</span>
                    {s.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-40 group-hover:translate-x-0 transition-all duration-200" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="py-6 border-t border-zinc-800/40">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-zinc-600 text-xs font-['JetBrains_Mono'] tracking-wide">
              &copy; {new Date().getFullYear()} Propix3ls. All rights reserved.
            </p>

            <div className="flex flex-wrap justify-center items-center gap-x-1 gap-y-2">
              {legalLinks.map((l, i) => (
                <span key={l.to} className="flex items-center">
                  <Link
                    to={l.to}
                    className="text-zinc-600 hover:text-zinc-300 text-xs font-['Manrope'] transition-colors duration-200 px-2 py-1 rounded-md hover:bg-white/[0.02]"
                    data-testid={l.testId}
                  >
                    {l.label}
                  </Link>
                  {i < legalLinks.length - 1 && (
                    <span className="text-zinc-800 text-xs">&middot;</span>
                  )}
                </span>
              ))}
            </div>

            {/* Back to top */}
            <button
              onClick={scrollToTop}
              className="hidden md:flex items-center gap-1.5 text-zinc-600 hover:text-zinc-300 text-xs font-['Manrope'] transition-colors duration-200 group"
              aria-label="Back to top"
            >
              <span>Top</span>
              <svg
                className="w-3 h-3 rotate-180 group-hover:-translate-y-0.5 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
